/**
 * 前端图片 URL 尺寸处理工具 + 商品图片字段归一
 *
 * 用法：import { getImageUrl } from '@/utils/image'
 *       getImageUrl(product.image, 'thumb')   // 列表缩略
 *       getImageUrl(product.image, 'medium')  // 详情中图
 *       getImageUrl(product.image)            // 原图预览
 *
 * COS 图片自动缩略（imageMogr2），本地图片原样（CSS object-fit 缩放）。
 */

const COS_SUFFIX = {
  thumb: '?imageMogr2/thumbnail/120x120/format/webp/quality/85',
  medium: '?imageMogr2/thumbnail/400x400/format/webp/quality/90',
  large: '?imageMogr2/thumbnail/750x750/format/webp/quality/90',
  original: ''
}

export function getImageUrl (url, size = 'original') {
  if (!url) return ''
  if (typeof url !== 'string' || !url.includes('myqcloud.com')) return url
  // 已带参数/已缩略的 URL 原样返回，防二次拼后缀
  if (url.includes('imageMogr2') || url.includes('?')) return url
  return url + (COS_SUFFIX[size] || '')
}

/**
 * 商品图片字段归一（2026-09-19 P0 图片收口）：全前端唯一入口。
 * 禁止页面自行 JSON.parse / split / image 三元 fallback——历史多形态只在此 normalize 一次。
 *
 * 真值：products.images（JSON URL 数组）+ products.image ≡ images[0]（服务端强制同步，
 * 第 1 张即主图，无第二个主图标记）。存量：52 件 image 单字符串形态（images NULL/'[]'）
 * ——读取时 fallback [image]；写路径（image-admin API）惰性迁移到数组形态。
 * 后端同语义镜像：server/routes/image-admin.js resolveImages。
 *
 * @param {{image?: string, images?: string|Array}} product 任意携带图片字段的商品对象
 * @returns {{url: string}[]} 恒为对象数组（按正式排序，[0] 即主图；无图返回 []）
 */
export function resolveProductImages (product) {
  if (!product) return []
  let urls = []
  const raw = product.images
  if (Array.isArray(raw)) {
    urls = raw.filter(u => typeof u === 'string' && u)
  } else if (typeof raw === 'string' && raw) {
    try {
      const v = JSON.parse(raw)
      urls = Array.isArray(v) ? v.filter(u => typeof u === 'string' && u) : []
    } catch (_) { urls = [] }
  }
  // 存量单图形态：images 空/无效但 image 有值 → [image]（image ≡ images[0]）
  if (!urls.length && typeof product.image === 'string' && product.image) {
    urls = [product.image]
  }
  return urls.map(url => ({ url }))
}

/** 便捷出口：URL 字符串数组（编辑表单 / 网格消费，保序，[0] 即主图） */
export function resolveProductImageUrls (product) {
  return resolveProductImages(product).map(i => i.url)
}

/** 便捷出口：主图 URL（卡片缩略消费；无图返回 ''，由调用方给占位图） */
export function resolveProductImageUrl (product) {
  const arr = resolveProductImages(product)
  return arr.length ? arr[0].url : ''
}

/**
 * 裁切框最大化算法（2026-09-19 裁切交互统一整改）：ImageUploader 与
 * AdminProductEdit 裁切器共用的唯一初始化规则——图片加载后 crop box
 * 默认即「原图（渲染区）内能容纳的最大 targetAspect 矩形」，用户打开
 * 后第一步直接拖动构图，不再先手动放大 200×200 默认小框。
 *
 *   sourceAspect > targetAspect → 高度吃满，width = imgH * aspect，水平居中
 *   sourceAspect < targetAspect → 宽度吃满，height = imgW / aspect，垂直居中
 *   sourceAspect == targetAspect → 整图
 *
 * 等价式 width = min(imgW, imgH * aspect)。居中/夹取由 vue-cropper
 * goAutoCrop(w,h)→changeCrop 内部完成（centerBox 保证框不越图片 = 不露边）。
 *
 * @param {number} imgW 渲染区宽（trueWidth * scale，旋转宽高已交换）
 * @param {number} imgH 渲染区高
 * @param {number} targetAspect 目标宽高比（w/h）；free 场景传 imgW/imgH（即整图）
 * @returns {{width:number, height:number}|null 尺寸不足时 null
 */
export function computeMaxCropRect (imgW, imgH, targetAspect) {
  if (!(imgW > 0) || !(imgH > 0) || !(targetAspect > 0)) return null
  const w = Math.min(imgW, imgH * targetAspect)
  const h = w / targetAspect
  if (w < 1 || h < 1) return null
  return { width: Math.floor(w), height: Math.max(1, Math.floor(h)) }
}

export default { getImageUrl, resolveProductImages, resolveProductImageUrls, resolveProductImageUrl, computeMaxCropRect }
